import { useState } from 'react';

import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { useToast } from '@/components/shared/Toast';
import { mutationErrorMessage } from '@/features/common/apiError';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RESOURCE_TYPE_ORDER, resourceTypeLabel } from './ResourceTypeBadge';
import { useCreateResourceMutation } from '../api/projectsApi';
import type { ProjectResourceType } from '../types';

/** One-line description per type at the point of decision (design §12/D4). */
const TYPE_HELP: Record<ProjectResourceType, string> = {
  meeting: 'Recorded or live meeting room',
  planning: 'Planning board or roadmap',
  'storage-collection': 'Storage collection of files',
  repository: 'Source repository',
  conversation: 'Conversation or thread',
  'indexer-source': 'Feed the indexer follows',
  voice: 'Voice channel or call',
  file: 'A single file',
};

export interface CreateResourceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}

/**
 * Add resource dialog (design §12).
 *
 * Contract note (design Q4): the projects service attaches **existing** objects —
 * `POST /projects/:id/resources` requires `{type, refId}` (the id of the meeting,
 * collection, repository, …), with `name` optional. So the dialog collects the
 * reference id (the platform has no resource-search endpoint in P1, same
 * constraint as the user id in §3.1) and an optional display name. The unique
 * key is `{projectId, type, refId}`, and the service answers `created: false` for
 * a repeat attach — which still reads as success here because the row exists.
 */
export function CreateResourceDialog({ open, onOpenChange, projectId }: CreateResourceDialogProps) {
  const toast = useToast();
  const [type, setType] = useState<ProjectResourceType>('meeting');
  const [refId, setRefId] = useState('');
  const [name, setName] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [createResource, { isLoading }] = useCreateResourceMutation();

  async function handleSubmit() {
    const trimmedRef = refId.trim();
    if (!trimmedRef) {
      setFieldError('Enter the resource ID.');
      return;
    }
    setFieldError(null);

    try {
      await createResource({
        projectId,
        type,
        refId: trimmedRef,
        ...(name.trim() ? { name: name.trim() } : {}),
      }).unwrap();
      toast.success('Resource added.');
      setRefId('');
      setName('');
      onOpenChange(false);
    } catch (error) {
      toast.error(mutationErrorMessage(error, "Couldn't add resource."));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!isLoading) {
          onOpenChange(next);
        }
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add resource</DialogTitle>
          <DialogDescription>Attach something the project works with.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="resource-type">Type</Label>
            <Select
              value={type}
              disabled={isLoading}
              onValueChange={(value) => setType(value as ProjectResourceType)}
            >
              <SelectTrigger id="resource-type" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {RESOURCE_TYPE_ORDER.map((value) => (
                  <SelectItem key={value} value={value}>
                    {resourceTypeLabel(value)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">{TYPE_HELP[type]}</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="resource-ref-id">Resource ID</Label>
            <Input
              id="resource-ref-id"
              value={refId}
              onChange={(event) => {
                setRefId(event.target.value);
                if (fieldError) {
                  setFieldError(null);
                }
              }}
              placeholder="e.g. mtg_4kd9x2ra"
              className="font-mono text-xs"
              disabled={isLoading}
              aria-invalid={fieldError ? true : undefined}
              aria-describedby={fieldError ? 'resource-ref-error' : 'resource-ref-help'}
            />
            {fieldError ? (
              <p
                id="resource-ref-error"
                role="alert"
                className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-foreground"
              >
                {fieldError}
              </p>
            ) : (
              <p id="resource-ref-help" className="text-sm text-muted-foreground">
                The ID of the {resourceTypeLabel(type).toLowerCase()} to attach. Ask its owner or
                your administrator.
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="resource-name">Name (optional)</Label>
            <Input
              id="resource-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="e.g. Weekly sync"
              disabled={isLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isLoading}>
            {isLoading ? (
              <LoadingSpinner size="sm" className="mr-2" label="Adding resource" />
            ) : null}
            {isLoading ? 'Adding…' : 'Add resource'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
